import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WokinghamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wokingham Pothole Claims | 2,817 Repairs in One Year | Fixtyer"
        description="Wokingham filled 2,817 potholes in 2023/24 on an 805km network, yet U-road RED readings swing from 3% to 21%. GREEN condition scorecard, AMBER overall. See what the DfT ratings mean for your pothole claim."
        canonical="/councils/wokingham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Wokingham', url: '/councils/wokingham' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Wokingham has a GREEN condition scorecard — can I still claim?',
            answer: 'Yes. The DfT Condition rating reflects classified-road SCANNER data, where A-roads have sat at 3% RED for five straight years and B/C roads at 73–78% Green. Your claim turns on the specific defect under Section 58, not a headline scorecard. On unclassified roads — 524.7km, 65% of the network — only RED is published under a different CVI survey, with readings swinging from 3% to 21% in consecutive years. Reactive maintenance also rose to 28% of spend in 2024/25, and the council acknowledges incidents can arise from low-risk defects it chooses not to repair.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 524.7km — roughly two-thirds of Wokingham\'s 805.4km carriageway network. The council surveys them by Coarse Visual Inspection (CVI), with half the network surveyed each year and each individual road assessed every two years. At the last five surveys, RED U-road share ranged from 3% (2021) to 21% (2022). Amber and Green categories are not reported for U-roads, so you cannot see how much of the residential network is in moderate condition — only the worst tier.',
          },
          {
            question: 'Why did U-road RED jump from 3% to 21% and back again?',
            answer: 'Wokingham\'s own report publishes only the RED percentage for unclassified roads, surveyed biennially by CVI rather than SCANNER. The figures are 15% (2020), 3% (2021), 21% (2022), 8% (2023) and 13% (2024). Because only half the U-road network is surveyed each year and the methodology differs from classified roads, year-on-year swings do not necessarily mean your street improved or collapsed — but they do show the council\'s published knowledge of U-road condition is patchy and volatile, which matters when arguing whether it had reasonable notice of defects on your route.',
          },
          {
            question: 'Does Wokingham\'s £2.38m Highway Investment Programme help my claim?',
            answer: 'It shows the council invests beyond DfT grant — but not necessarily on your road. Since 2019 the council has borrowed an extra circa £2.38m per year for preventative treatments. Yet in 2025/26 projected carriageway capital spend (£5,721k) essentially matches the DfT allocation (£5,719k), with much of the wider £10.877m capital programme going to bridges (£3,298k) and other assets. The DfT Spend scorecard is AMBER. For your claim, the relevant question is whether preventative work reached the specific road where you were damaged, not whether the borough runs a supplementary investment programme in aggregate.',
          },
          {
            question: 'Wokingham allows temporary pothole repairs — does that affect my claim?',
            answer: 'Yes, potentially in your favour. The council\'s transparency report states that when a permanent repair is not possible, "a make safe or temporary repair within the emergency or urgent response time is acceptable" under the Highways Maintenance Management Plan. If the pothole that damaged your vehicle had been temporarily patched, failed again, or was classified as low-risk under the council\'s risk-based strategy, that is evidence the defect was known but not permanently fixed — central to both Section 41 liability and rebutting a Section 58 defence.',
          },
          {
            question: 'Who maintains the A329(M) through Wokingham?',
            answer: 'Wokingham Borough Council is one of only a few local authorities that maintains a section of motorway — the A329(M) between Coppid Beech and Winnersh, providing access to junction 10 of the M4. This 118.6km A-road and motorway category is included in the council\'s annual SCANNER surveys, where RED condition has remained at 3% from 2020 to 2024. If your incident was on the A329(M), expect the council to point to stable classified-road data; your claim should still focus on inspection logs, repair history and prior reports for the specific carriageway section.',
          },
          {
            question: 'Pothole repairs fell after the 2023/24 peak — are the roads fixed?',
            answer: 'Wokingham still filled an estimated 2,052 potholes in 2024/25 and projects roughly 2,000 in 2025/26 alongside 4,440 other carriageway and footway defects. The five-year total is 10,967 pothole repairs on 805.4km of road. The council notes some work orders cover multiple potholes while others may not be recorded separately, so individual defect counts may differ. A network producing thousands of potholes annually is one where new defects routinely form between inspections.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Wokingham Borough Council"
        councilSlug="wokingham"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/wokingham"
        headline="Wokingham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Wokingham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Wokingham: 2,817 pothole repairs in one year on 805km of road
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Wokingham earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards for road condition
            and best practice on its classified network — yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> because spend barely matches allocation.
            Meanwhile an estimated <strong className="text-rose-400">2,817 potholes were filled in 2023/24</strong>,
            and RED readings on unclassified roads — 65% of the borough — have swung from 3% to 21% in a single year.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">10,967</div>
            <div className="text-white font-medium mt-2">Estimated pothole repairs in five years</div>
            <div className="text-brand-300 text-sm mt-1">
              From 2,069 in 2020/21 to a peak of 2,817 in 2023/24 — on a network valued at £1.452 billion
              serving 177,500 residents across 179 square kilometres.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Wokingham Borough Council&apos;s June 2025 transparency report —
            stable classified roads, volatile unclassified readings
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads and A329(M) motorway (118.6km — 14.7% of network): flat</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">unchanged since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">20%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 19% in 2021</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">77%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">broadly flat at 76–78%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Wokingham is one of only a few authorities maintaining a motorway section — the A329(M) between
              Coppid Beech and Winnersh. A-roads and motorways are surveyed every year by SCANNER. Condition
              has barely moved: 3% RED, roughly one-fifth Amber, three-quarters Green across five years.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">B and C roads (162.1km — 20.1% of network): stable Green majority</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">78%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold">23%</td>
                    <td className="px-4 py-2 text-center font-bold">73%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 font-medium">
              B and C roads are surveyed annually. Roughly three-quarters remain in Green condition — this is
              the data behind Wokingham&apos;s GREEN DfT Condition scorecard. Claims on classified roads face a
              stronger council defence, but 27% Amber plus 4% RED still means maintenance may be required on
              almost a third of the B/C network.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Why spend is AMBER despite extra borrowing</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£5.719m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">£5.721m</div>
                <div className="text-brand-300 text-sm">Projected carriageway capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.38m</div>
                <div className="text-brand-300 text-sm">Annual Highway Investment Programme (since 2019)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Carriageway capital spend in 2025/26 essentially matches the DfT grant — unlike authorities that
              spend well above allocation. The council supplements with circa{' '}
              <span className="text-emerald-400">£2.38m per year</span> from additional capital borrowing for
              preventative treatments, but much of the wider £10.877m capital programme goes to bridges (£3.298m),
              drainage and other assets. Reactive maintenance rose to{' '}
              <span className="text-amber-400">28%</span> of carriageway spend in 2024/25.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 524.7km CVI blind spot
          </h2>
          <p className="text-surface-600 mb-8">
            65% of Wokingham&apos;s carriageway network is unclassified — surveyed by a different method, on a
            two-year cycle, with only RED published
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">3%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">8%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Coarse Visual Inspection, not SCANNER</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wokingham&apos;s report explains that SCANNER laser surveys are not appropriate for most U-roads
                — they may be narrow, have parked vehicles, or use block paving. Instead the council uses Coarse
                Visual Inspection (CVI) from a slow-moving vehicle, recording defect extent and position.
              </p>
              <p className="text-surface-600 text-sm">
                Half (50%) of the U-road network is surveyed by CVI each year, meaning each unclassified road
                is assessed every <strong>two years</strong>. Amber and Green categories are not reported for
                U-roads — only RED — so the published picture of residential-road condition is incomplete.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 3% to 21% swing</h3>
              <p className="text-amber-800 text-sm mb-3">
                RED U-road share fell from 15% to 3% between 2020 and 2021, then jumped to 21% in 2022 —
                before settling at 8% (2023) and 13% (2024). On a biennial CVI cycle covering half the network
                annually, these swings reflect sampling and methodology as much as physical change.
              </p>
              <p className="text-amber-800 text-sm">
                At 13% RED in 2024, roughly 68km of unclassified roads should be considered for maintenance.
                At the 2022 peak of 21%, that would be approximately 110km of residential and minor rural routes.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Wokingham&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last CVI-surveyed — and was it in a year when that half of the network was covered?</li>
              <li>• If only RED is published, how did the council assess Amber-level defects that could become potholes?</li>
              <li>• Does the council&apos;s claim of annual safety inspections on every road match the biennial condition survey?</li>
              <li>• Can year-on-year RED swings from 3% to 21% support a defence that the council knew your road&apos;s condition?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot point to GREEN classified-road scorecards while two-thirds of its network is
              measured on a different ruler, on a two-year cycle, with only the worst tier published.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repair figures */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            10,967 pothole repairs in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated pothole fills from Wokingham&apos;s own transparency report — peaking after wet winters
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated pothole repairs</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Notes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,069</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,449</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,580</td>
                  <td className="px-6 py-4 text-center text-surface-500">Lowest year</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,817</td>
                  <td className="px-6 py-4 text-center text-rose-600 font-medium">Five-year peak</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,052</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,967</td>
                  <td className="px-6 py-4 text-center text-surface-500">2025/26 projected: ~2,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~6 potholes a day, every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Wokingham fills roughly six potholes per day on 805.4km of road.
                The council projects approximately 2,000 pothole repairs in 2025/26 alongside 4,440 other
                carriageway and footway defects. A network producing defects at this rate is one where potholes
                routinely form between inspections — exactly where prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The counting caveat</h3>
              <p className="text-amber-800 text-sm">
                Wokingham&apos;s footnote: figures are estimates based on minor works orders, some of which may
                repair more than one pothole but this is not always recorded. Most repairs are permanent, but
                when that is not possible the Highways Maintenance Management Plan allows a make-safe or
                temporary repair within emergency or urgent response times — repairs that can fail and recur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Risk-based strategy admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The risk-based repair admission
          </h2>
          <p className="text-surface-600 mb-8">
            Wokingham&apos;s own words on how it prioritises — and deprioritises — defect repairs
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Identification and timescale for defect repair follows a risk-based strategy which prioritises the worst defects for repair based on site factors. Incidents can or could still arise from low-risk defects, but the strategy acknowledges that from the outset."
              source="Wokingham Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="We estimate we will fill approximately 2,000 potholes in 2025/26 alongside 4,440 other carriageway and footway defects."
              source="Wokingham Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wokingham formally acknowledges that its risk-based hierarchy means not every defect gets
                repaired — and that incidents can arise from defects classified as low-risk. The council
                states pothole severity depends on depth, surface area, safety of road users, and location
                and usage of the road.
              </p>
              <p className="text-surface-600 text-sm">
                If the pothole that damaged your vehicle was below the council&apos;s repair threshold at
                inspection but large enough to cause harm when you hit it, that gap between risk classification
                and actual harm is central to your claim.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your pothole classified as low-risk when inspected — and if so, on what date?</li>
                <li>• Had a prior report been logged before your incident, triggering a higher risk category?</li>
                <li>• Was a temporary or make-safe repair applied that subsequently failed?</li>
                <li>• Does the 28-day non-urgent response target mean your defect sat unrepaired for weeks?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Streetworks and climate */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Streetworks pressure and water damage
          </h2>
          <p className="text-surface-600 mb-8">
            External factors Wokingham identifies as accelerating network deterioration
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Wokingham's road network faces high demand for street and road works, and our authority is committed to minimising disruption to residents, businesses, and road users through a robust programme of planning, coordination, and communication."
              source="Wokingham Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="Wetter periods and extreme rainfall event frequencies coupled with reductions in other activities such as street cleansing means more than ever, we must focus on ensuring the cyclical programme of cleansing gullies and catchpits is effective."
              source="Wokingham Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Utility works and reinstatements</h3>
              <p className="text-surface-600 text-sm">
                The council revised its Highways &amp; Transport team structure in 2023 to align streetworks
                management with traffic duties, extended its permit scheme across the full network, and uses
                One.Network for coordination. It states a priority is &ldquo;minimising defects to our asset
                caused by works&rdquo; — an admission that third-party works can damage the carriageway. If
                your pothole formed at a utility reinstatement, that is a distinct liability angle worth exploring.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Water ingress and freeze-thaw</h3>
              <p className="text-amber-800 text-sm">
                Wokingham identifies wetter periods and extreme rainfall as factors requiring greater gully
                and catchpit cleansing, with analysis of tree-lined roads and lidar data to target problem
                sites. The council reintroduced crack sealing on the A329(M) in 2025/26 specifically because
                reactive repairs there are expensive due to traffic management — early intervention to prevent
                defects becoming longer-term problems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN condition means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN-condition, AMBER-overall council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Wokingham is better run than many authorities — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition scorecard — 73–78% of B/C roads in Green, A-roads stable at 3% RED
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — asset management strategy aligned to Well-managed Highway Infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> APSE nominated best performer for highways and winter maintenance (2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Supplementary £2.38m annual investment programme since 2019
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 190,000 m² surface dressing and 42,000 m² resurfacing planned for 2025/26
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on A-roads and B/C routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall and AMBER spend — carriageway capital barely matches DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 65% of network on biennial CVI survey with only RED published
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED swung from 3% to 21% — volatile published knowledge of residential roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10,967 pothole fills in five years — defects form faster than prevention eliminates them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Risk-based strategy admits incidents from low-risk defects; temporary repairs permitted
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive maintenance at 28% of spend in 2024/25 — up from 19% in 2022/23
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and best-practice scorecards, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online form) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the biennial CVI gap and RED-only reporting are your strongest structural arguments</li>
              <li>• Whether a temporary repair was applied and failed before your incident</li>
              <li>• Proximity to recent streetworks visible on One.Network</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Wokingham&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Wokingham first
          </h2>
          <p className="text-surface-600 mb-6">
            Wokingham&apos;s transparency report states that some defects are reported by residents and many
            are identified through annual safety inspections, with repair following a risk-based strategy.
            Reporting the defect through the council creates a dated record — useful evidence if the pothole
            was logged before your incident, or if the council failed to repair it within its 28-day non-urgent
            target or 2-hour urgent response.
          </p>
          <a
            href="https://www.wokingham.gov.uk/roads/report/report-pothole-and-roadfootway-damage"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Wokingham Borough Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the online map for non-urgent potholes. For dangerous defects, call 0118 974 6000 (Mon–Fri 9am–5pm)
            or 0800 212 111 out of hours. Keep your reference number to track the fault.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Wokingham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road CVI survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No temporary-repair failure angle</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 524.7km U-road CVI gap documented</li>
                <li>✅ 10,967 pothole fills in five years cited</li>
                <li>✅ Risk-based repair admission argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Wokingham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=wokingham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Wokingham claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wokingham has a GREEN condition scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Condition rating reflects classified-road SCANNER data, where A-roads have sat at
                3% RED for five straight years and B/C roads at 73–78% Green. Your claim turns on the specific
                defect under Section 58, not a headline scorecard. On unclassified roads — 524.7km, 65% of the
                network — only RED is published under a different CVI survey, with readings swinging from 3%
                to 21% in consecutive years.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 524.7km — roughly two-thirds of Wokingham&apos;s 805.4km carriageway network.
                The council surveys them by Coarse Visual Inspection, with half the network surveyed each year
                and each individual road assessed every two years. At the last five surveys, RED U-road share
                ranged from 3% (2021) to 21% (2022). Amber and Green categories are not reported for U-roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did U-road RED jump from 3% to 21% and back again?
              </h3>
              <p className="text-surface-600 text-sm">
                Wokingham publishes only the RED percentage for unclassified roads, surveyed biennially by
                CVI rather than SCANNER. The figures are 15% (2020), 3% (2021), 21% (2022), 8% (2023) and
                13% (2024). Because only half the U-road network is surveyed each year and the methodology
                differs from classified roads, year-on-year swings do not necessarily mean your street improved
                or collapsed — but they do show published knowledge of U-road condition is patchy.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wokingham&apos;s £2.38m Highway Investment Programme help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It shows the council invests beyond DfT grant — but not necessarily on your road. Since 2019
                the council has borrowed an extra circa £2.38m per year for preventative treatments. Yet in
                2025/26 projected carriageway capital spend (£5.721m) essentially matches the DfT allocation
                (£5.719m). The DfT Spend scorecard is AMBER. For your claim, the question is whether
                preventative work reached the specific road where you were damaged.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wokingham allows temporary pothole repairs — does that affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes, potentially in your favour. The council states that when a permanent repair is not
                possible, a make-safe or temporary repair within the emergency or urgent response time is
                acceptable under the Highways Maintenance Management Plan. If the pothole had been temporarily
                patched and failed again, that is evidence the defect was known but not permanently fixed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Who maintains the A329(M) through Wokingham?
              </h3>
              <p className="text-surface-600 text-sm">
                Wokingham Borough Council maintains the A329(M) between Coppid Beech and Winnersh — one of
                only a few local authorities responsible for a motorway section. This is included in the
                118.6km A-road and motorway category surveyed annually, where RED condition has remained at
                3% from 2020 to 2024. Claims on the A329(M) should still focus on inspection logs and repair
                history for the specific section.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell after the 2023/24 peak — are the roads fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                Wokingham still filled an estimated 2,052 potholes in 2024/25 and projects roughly 2,000 in
                2025/26 alongside 4,440 other carriageway and footway defects. The five-year total is 10,967
                pothole repairs on 805.4km of road. The council notes some work orders cover multiple potholes
                while others may not be recorded separately.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.wokingham.gov.uk/sites/wokingham/files/2025-06/Local%20highways%20maintenance%20transparency%20report_0.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Wokingham Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
